/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import swal from "sweetalert";

import { saveAs } from "file-saver";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import Chip from "@mui/material/Chip";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDropzone from "components/MDDropzone";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function AsetList() {
  const [menu, setMenu] = useState(null);
  const dropzoneRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid);
    setDinasId(user.dinas_id);
  }, []);

  const [form, setForm] = useState({
    kodeBarang: "",
    register: "",
    nama_barang: "",
    merk_type: "",
    no_identitas_barang: "",
    bahan: "",
    asal_perolehan: "",
    tahun_perolehan: "",
    ukuran_barang: "",
    satuan: "",
    keadaan_barang: "",
    jumlah_barang: "",
    harga_satuan: "",
    keterangan: "",
    jenis_barang: "",
    lokasi: "",
    holder_id: "",
    idAdmin: userId,
    idDinas: dinasId,
  });
  const [editId, setEditId] = useState(null);
  const [filterKeadaan, setFilterKeadaan] = useState("All");
  const [search, setSearch] = useState("");
  const [aset, setAset] = useState([]);
  const [selectedSatuan, setSelectedSatuan] = useState(null);
  const [selectedKeadaan, setSelectedKeadaan] = useState(null);
  const [selectedBahan, setSelectedBahan] = useState(null);
  const [selectedAsalPerolehan, setSelectedAsalPerolehan] = useState(null);
  const [selectedJenisBarang, setSelectedJenisBarang] = useState(null);
  const [selectedKecamatan, setSelectedKecamatan] = useState(null);
  const [selectedDesa, setSelectedDesa] = useState(null);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [desaData, setDesaData] = useState([]);
  const [userList, setUserList] = useState([]);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [showSizeWarning, setShowSizeWarning] = useState(false);

  const keadaanOptions = ["Baik", "Rusak Ringan", "Rusak Berat", "Hilang"];
  const satuanOptions = [
    "Unit",
    "Buah",
    "Set",
    "Lembar",
    "Meter",
    "Liter",
    "Kilogram",
    "Gram",
    "Ton",
    "Centimeter",
    "Millimeter",
    "Kilometer",
    "Meter Persegi",
    "Meter Kubik",
    "Centimeter Persegi",
    "Centimeter Kubik",
    "Mililiter",
    "Gallon",
    "Pint",
    "Quart",
    "Pak",
    "Box",
    "Kardus",
    "Botol",
    "Kaleng",
    "Dus",
    "Rim",
    "Kodi",
    "Gross",
    "Lusin",
    "Pasang",
    "Keping",
    "Helai",
    "Batang",
    "Gulung",
    "Meteran",
    "Yard",
    "Inch",
    "Feet",
    "Mile",
    "Acre",
    "Hektar",
    "Watt",
    "Volt",
    "Ampere",
    "Hertz",
    "Byte",
    "Kilobyte",
    "Megabyte",
    "Gigabyte",
    "Terabyte",
  ];
  const bahanOptions = ["Besi", "Kayu", "Plastik", "Kain", "Kaca"];
  const asalPerolehanOptions = [
    "Dana Hibah",
    "APBD",
    "Pengadaan/Pembelian",
    "Lainnya",
  ];
  const jenisBarangOptions = ["Barang Bergerak", "Barang Tidak Bergerak"];

  const dropzoneOptions = useMemo(
    () => ({
      addRemoveLinks: true,
      maxFiles: 5,
      maxFilesize: 5,
      acceptedFiles: ".jpeg,.jpg,.png",
      autoProcessQueue: false,
    }),
    []
  );

  const fetchAset = () => {
    setLoading(true);
    axios
      .get(`${API_ESA_OFFICE}aset/dinas/${dinasId}`)
      .then((res) => {
        setAset(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const fetchDesa = async () => {
    try {
      const response = await axios.get(`${API_ESA_OFFICE}desa`);
      setDesaData(response.data || []);
    } catch (error) {
      console.error("Error fetching desa data:", error);
      setDesaData([]);
    }
  };

  const fetchUserList = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/dinas/${dinasId}`
      );
      const formattedUsers = response.data.map((user) => ({
        value: user.user_id,
        label: user.nama,
        avatar: user.profil ? `${API_ESA_OFFICE_UPLOAD}${user.profil}` : null,
      }));
      setUserList(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUserList([]);
    }
  };

  useEffect(() => {
    fetchDesa();
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchUserList();
      fetchAset();
    }
  }, [dinasId]);

  // Fungsi untuk menangani klik pada foto
  const handleImageClick = (images) => {
    setSelectedImages(images);
    setOpenImageDialog(true);
  };

  // Definisikan kolom DataTable
  const columns = [
    {
      Header: "No",
      accessor: (row, index) => index + 1,
      width: "3%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Kode Barang",
      accessor: "kodeBarang",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Register",
      accessor: "register",
      width: "7%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Nama Barang",
      accessor: "nama_barang",
      width: "12%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Merk/Type",
      accessor: "merk_type",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "No. Identitas",
      accessor: "no_identitas_barang",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Bahan",
      accessor: "bahan",
      width: "6%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Asal Perolehan",
      accessor: "asal_perolehan",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Tahun",
      accessor: "tahun_perolehan",
      width: "5%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Ukuran",
      accessor: "ukuran_barang",
      width: "5%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Satuan",
      accessor: "satuan",
      width: "5%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Keadaan",
      accessor: "keadaan_barang",
      width: "6%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Jumlah",
      accessor: "jumlah_barang",
      width: "5%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Harga Satuan",
      accessor: "harga_satuan",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">
          {value ? `Rp ${parseInt(value).toLocaleString("id-ID")}` : "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Jenis Barang",
      accessor: "jenis_barang",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value || "-"}</MDTypography>
      ),
    },
    {
      Header: "Lokasi",
      accessor: "lokasi",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value || "-"}</MDTypography>
      ),
    },
    {
      Header: "Pemegang",
      accessor: "nama_holder",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value || "-"}</MDTypography>
      ),
    },
    {
      Header: "Admin",
      accessor: "nama_admin",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value}</MDTypography>
      ),
    },
    {
      Header: "Keterangan",
      accessor: "keterangan",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">{value || "-"}</MDTypography>
      ),
    },
    {
      Header: "Foto",
      accessor: "foto",
      width: "8%",
      Cell: ({ row }) => {
        const fotoArray = row.original.foto
          ? JSON.parse(row.original.foto)
          : [];
        return (
          <MDBox display="flex" gap={1}>
            {fotoArray.slice(0, 2).map((foto, index) => (
              <img
                key={index}
                src={`${API_ESA_OFFICE_UPLOAD}${foto}`}
                alt={`Foto ${index + 1}`}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "4px",
                }}
                onClick={() => handleImageClick(fotoArray)}
              />
            ))}
            {fotoArray.length > 2 && (
              <MDBox
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "grey.200",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => handleImageClick(fotoArray)}
              >
                <MDTypography variant="caption">
                  +{fotoArray.length - 2}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        );
      },
    },
    {
      Header: "QR Code",
      accessor: "qrcode",
      width: "6%",
      Cell: ({ row }) => (
        <MDBox>
          {row.original.qrcode ? (
            <img
              src={`${API_ESA_OFFICE_UPLOAD}${row.original.qrcode}`}
              alt="QR Code"
              style={{ width: 40, height: 40 }}
            />
          ) : (
            <MDTypography variant="caption">Loading...</MDTypography>
          )}
        </MDBox>
      ),
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox>
          <IconButton
            color="success"
            onClick={() => handleViewDetail(row.original.id)}
            size="small"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            color="info"
            onClick={() => handleEdit(row.original)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(row.original.id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </MDBox>
      ),
    },
  ];

  // Data baris, filter sesuai filterKeadaan dan search
  const filteredRows = useMemo(() => {
    return aset
      .filter((item) =>
        filterKeadaan === "All" ? true : item.keadaan_barang === filterKeadaan
      )
      .filter((item) =>
        item.nama_barang.toLowerCase().includes(search.trim().toLowerCase())
      )
      .map((item) => ({
        ...item,
      }));
  }, [aset, filterKeadaan, search]);

  const handleOpenDialog = () => {
    setForm({
      kodeBarang: "",
      register: "",
      nama_barang: "",
      merk_type: "",
      no_identitas_barang: "",
      bahan: "",
      asal_perolehan: "",
      tahun_perolehan: "",
      ukuran_barang: "",
      satuan: "",
      keadaan_barang: "",
      jumlah_barang: "",
      harga_satuan: "",
      keterangan: "",
      jenis_barang: "",
      lokasi: "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(null);
    setSelectedKeadaan(null);
    setSelectedBahan(null);
    setSelectedAsalPerolehan(null);
    setSelectedJenisBarang(null);
    setSelectedKecamatan(null);
    setSelectedDesa(null);
    setSelectedHolder(null);
    setIsEdit(false);
    setEditId(null);
    setQrCodeData(null);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
    setOpenDialog(true);
  };

  const handleEdit = (data) => {
    const satuanData = satuanOptions.find((s) => s === data.satuan);
    const keadaanData = keadaanOptions.find((k) => k === data.keadaan_barang);
    const bahanData = bahanOptions.find((b) => b === data.bahan);
    const asalPerolehanData = asalPerolehanOptions.find(
      (a) => a === data.asal_perolehan
    );
    const jenisBarangData = jenisBarangOptions.find(
      (j) => j === data.jenis_barang
    );

    // Parse lokasi untuk mendapatkan kecamatan dan desa
    let kecamatanData = null;
    let desaData = null;
    if (data.lokasi) {
      const lokasiParts = data.lokasi.split(" - ");
      if (lokasiParts.length === 2) {
        kecamatanData = lokasiParts[0];
        desaData = lokasiParts[1];
      }
    }

    // Find holder data
    const holderData = userList.find((user) => user.value === data.holder_id);

    setForm({
      kodeBarang: data.kodeBarang || "",
      register: data.register || "",
      nama_barang: data.nama_barang || "",
      merk_type: data.merk_type || "",
      no_identitas_barang: data.no_identitas_barang || "",
      bahan: data.bahan || "",
      asal_perolehan: data.asal_perolehan || "",
      tahun_perolehan: data.tahun_perolehan || "",
      ukuran_barang: data.ukuran_barang || "",
      satuan: data.satuan || "",
      keadaan_barang: data.keadaan_barang || "",
      jumlah_barang: data.jumlah_barang || "",
      harga_satuan: data.harga_satuan || "",
      keterangan: data.keterangan || "",
      jenis_barang: data.jenis_barang || "",
      lokasi: data.lokasi || "",
      holder_id: data.holder_id || "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(satuanData);
    setSelectedKeadaan(keadaanData);
    setSelectedBahan(bahanData);
    setSelectedAsalPerolehan(asalPerolehanData);
    setSelectedJenisBarang(jenisBarangData);
    setSelectedKecamatan(kecamatanData);
    setSelectedDesa(desaData);
    setSelectedHolder(holderData);
    setIsEdit(true);
    setEditId(data.id);
    setQrCodeData(data);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data aset akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(`${API_ESA_OFFICE}aset/${id}`);
          swal("Berhasil!", "Data aset berhasil dihapus", "success");
          fetchAset();
        } catch (err) {
          swal("Error!", "Gagal menghapus aset", "error");
        }
      }
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setForm({
      kodeBarang: "",
      register: "",
      nama_barang: "",
      merk_type: "",
      no_identitas_barang: "",
      bahan: "",
      asal_perolehan: "",
      tahun_perolehan: "",
      ukuran_barang: "",
      satuan: "",
      keadaan_barang: "",
      jumlah_barang: "",
      harga_satuan: "",
      keterangan: "",
      jenis_barang: "",
      lokasi: "",
      holder_id: "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(null);
    setSelectedKeadaan(null);
    setSelectedBahan(null);
    setSelectedAsalPerolehan(null);
    setSelectedJenisBarang(null);
    setSelectedKecamatan(null);
    setSelectedDesa(null);
    setSelectedHolder(null);
    setIsEdit(false);
    setEditId(null);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    setTotalSize(totalSizeInMB);
    setShowSizeWarning(totalSizeInMB > 15);
    setFiles(files);
  };

  const handleRemove = useCallback((file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
  }, []);

  const handleSubmit = async () => {
    try {
      if (editId) {
        // Mode edit - kirim sebagai JSON
        const response = await axios.put(
          `${API_ESA_OFFICE}aset/${editId}`,
          form,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // Mode tambah - kirim sebagai FormData
        const formData = new FormData();

        // Append all form fields
        Object.keys(form).forEach((key) => {
          formData.append(key, form[key]);
        });

        // Append all files
        if (files && files.length > 0) {
          files.forEach((file) => {
            formData.append("foto", "file");
          });
        }

        const response = await axios.post(`${API_ESA_OFFICE}aset`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setQrCodeData(response.data);
      }

      swal("Berhasil!", "Data aset berhasil ditambahkan", "success");
      handleCloseDialog();
      fetchAset();
    } catch (error) {
      console.error("Error:", error);
      swal("Error!", "Gagal menyimpan data aset", "error");
    }
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        selected={filterKeadaan === "All"}
        onClick={() => {
          setFilterKeadaan("All");
          closeMenu();
        }}
      >
        Semua
      </MenuItem>
      {keadaanOptions.map((keadaan) => (
        <MenuItem
          key={keadaan}
          selected={filterKeadaan === keadaan}
          onClick={() => {
            setFilterKeadaan(keadaan);
            closeMenu();
          }}
        >
          {keadaan}
        </MenuItem>
      ))}
    </Menu>
  );

  // Fungsi export Excel
  const handleExportExcel = () => {
    if (!aset.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Kode Barang",
      "Register",
      "Nama Barang",
      "Merk/Type",
      "No. Identitas Barang",
      "Bahan",
      "Asal Perolehan",
      "Tahun Perolehan",
      "Ukuran Barang",
      "Satuan",
      "Keadaan Barang",
      "Jumlah Barang",
      "Harga Satuan",
      "Jenis Barang",
      "Lokasi",
      "Pemegang Barang",
      "Admin",
      "Keterangan",
      "Tanggal Dibuat",
    ];

    let tableRows = "";
    aset.forEach((item, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${item.kodeBarang || ""}</td>
          <td>${item.register || ""}</td>
          <td>${item.nama_barang || ""}</td>
          <td>${item.merk_type || ""}</td>
          <td>${item.no_identitas_barang || ""}</td>
          <td>${item.bahan || ""}</td>
          <td>${item.asal_perolehan || ""}</td>
          <td>${item.tahun_perolehan || ""}</td>
          <td>${item.ukuran_barang || ""}</td>
          <td>${item.satuan || ""}</td>
          <td>${item.keadaan_barang || ""}</td>
          <td>${item.jumlah_barang || ""}</td>
          <td>${
            item.harga_satuan
              ? `Rp ${parseInt(item.harga_satuan).toLocaleString("id-ID")}`
              : ""
          }</td>
          <td>${item.jenis_barang || ""}</td>
          <td>${item.lokasi || ""}</td>
          <td>${item.nama_holder || ""}</td>
          <td>${item.nama_admin || ""}</td>
          <td>${item.keterangan || ""}</td>
          <td>${
            item.createdAt
              ? new Date(item.createdAt).toLocaleDateString("id-ID")
              : ""
          }</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Data Aset</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Data Aset</h2>
          <p>Total Aset: ${aset.length} item</p>
          <p>Tanggal Export: ${new Date().toLocaleDateString("id-ID")}</p>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `aset-list-${new Date().toISOString().split("T")[0]}.xls`);
  };

  const handleViewDetail = (id) => {
    navigate(`/pages/aset/detail/${id}`);
  };

  // Mendapatkan opsi kecamatan unik dari data desa
  const kecamatanOptions = useMemo(() => {
    const kecamatans = [...new Set(desaData.map((item) => item.kecamatan))];
    return kecamatans.sort();
  }, [desaData]);

  // Mendapatkan opsi desa berdasarkan kecamatan yang dipilih
  const desaOptions = useMemo(() => {
    if (!selectedKecamatan) return [];
    const desas = desaData
      .filter((item) => item.kecamatan === selectedKecamatan)
      .map((item) => item.desa);
    return desas.sort();
  }, [desaData, selectedKecamatan]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox my={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDButton variant="gradient" color="info" onClick={handleOpenDialog}>
            Tambah Aset
          </MDButton>
          <MDBox display="flex">
            <MDButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              Filter Keadaan&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderMenu}
            <MDBox ml={1}>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleExportExcel}
              >
                <Icon>description</Icon>
                &nbsp;Export Excel
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Card>
          <MDBox p={2} pb={0} display="flex" alignItems="center" gap={2}>
            <TextField
              label="Cari Nama Barang"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 250 }}
            />
          </MDBox>
          <MDBox sx={{ overflowX: "auto" }}>
            <DataTable
              table={{ columns, rows: filteredRows }}
              entriesPerPage={false}
              canSearch
              isLoading={loading}
            />
          </MDBox>
        </Card>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{isEdit ? "Edit Aset" : "Tambah Aset Baru"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Kode Barang"
                  name="kodeBarang"
                  value={form.kodeBarang}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Register"
                  name="register"
                  value={form.register}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Nama Barang"
                  name="nama_barang"
                  value={form.nama_barang}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Merk/Type"
                  name="merk_type"
                  value={form.merk_type}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="No. Identitas Barang"
                  name="no_identitas_barang"
                  value={form.no_identitas_barang}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={bahanOptions}
                  value={selectedBahan}
                  onChange={(event, newValue) => {
                    setSelectedBahan(newValue);
                    setForm({ ...form, bahan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Bahan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={asalPerolehanOptions}
                  value={selectedAsalPerolehan}
                  onChange={(event, newValue) => {
                    setSelectedAsalPerolehan(newValue);
                    setForm({ ...form, asal_perolehan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Asal Perolehan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Tahun Perolehan"
                  name="tahun_perolehan"
                  value={form.tahun_perolehan}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Ukuran Barang"
                  name="ukuran_barang"
                  value={form.ukuran_barang}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={satuanOptions}
                  value={selectedSatuan}
                  onChange={(event, newValue) => {
                    setSelectedSatuan(newValue);
                    setForm({ ...form, satuan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Satuan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={keadaanOptions}
                  value={selectedKeadaan}
                  onChange={(event, newValue) => {
                    setSelectedKeadaan(newValue);
                    setForm({ ...form, keadaan_barang: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Keadaan Barang"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={jenisBarangOptions}
                  value={selectedJenisBarang}
                  onChange={(event, newValue) => {
                    setSelectedJenisBarang(newValue);
                    setForm({ ...form, jenis_barang: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Jenis Barang"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={kecamatanOptions}
                  value={selectedKecamatan}
                  onChange={(event, newValue) => {
                    setSelectedKecamatan(newValue);
                    setSelectedDesa(null); // Reset desa when kecamatan changes
                    setForm({ ...form, lokasi: "" }); // Reset lokasi when kecamatan changes
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Kecamatan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={desaOptions}
                  value={selectedDesa}
                  onChange={(event, newValue) => {
                    setSelectedDesa(newValue);
                    const lokasi =
                      selectedKecamatan && newValue ? `${newValue}` : "";
                    setForm({ ...form, lokasi });
                  }}
                  disabled={!selectedKecamatan}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Desa"
                      required
                      fullWidth
                      helperText={
                        !selectedKecamatan
                          ? "Pilih Kecamatan terlebih dahulu"
                          : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={userList}
                  value={selectedHolder}
                  onChange={(event, newValue) => {
                    setSelectedHolder(newValue);
                    setForm({ ...form, holder_id: newValue?.value || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Pemegang Barang"
                      required
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        avatar={
                          option.avatar ? (
                            <img
                              src={option.avatar}
                              alt={option.label}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                              }}
                            />
                          ) : null
                        }
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {option.avatar && (
                          <img
                            src={option.avatar}
                            alt={option.label}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                            }}
                          />
                        )}
                        <span>{option.label}</span>
                      </div>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Jumlah Barang"
                  name="jumlah_barang"
                  value={form.jumlah_barang}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Harga Satuan"
                  name="harga_satuan"
                  value={form.harga_satuan}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Keterangan"
                  name="keterangan"
                  value={form.keterangan}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                {useMemo(
                  () => (
                    <MDDropzone
                      options={{
                        addRemoveLinks: true,
                        uploadMultiple: true,
                        maxFilesize: 5, // dalam MB
                        maxFiles: 5, // maksimal 5 file
                        maxTotalFilesize: 15, // total maksimal 15MB
                        acceptedFiles: ".jpeg,.jpg,.png",
                        dictDefaultMessage:
                          "Klik atau seret gambar ke sini untuk mengunggah",
                        dictFileTooBig:
                          "Gambar terlalu besar ({{filesize}}MB). Maksimal ukuran file: {{maxFilesize}}MB",
                        dictTotalFilesTooBig:
                          "Total ukuran gambar melebihi batas maksimal (15MB)",
                        dictInvalidFileType:
                          "Format file tidak didukung. Hanya JPG, JPEG, dan PNG yang diperbolehkan",
                        dictResponseError:
                          "Terjadi kesalahan saat mengunggah gambar",
                        dictCancelUpload: "Batalkan unggah",
                        dictUploadCanceled: "Unggah dibatalkan",
                        dictCancelUploadConfirmation:
                          "Apakah Anda yakin ingin membatalkan unggah?",
                        dictRemoveFile: "Hapus gambar",
                        dictMaxFilesExceeded:
                          "Anda tidak dapat mengunggah gambar lagi",
                      }}
                      onChange={handleDropzoneChange}
                      ref={dropzoneRef}
                    />
                  ),
                  []
                )}
              </Grid>
              {files.length > 0 && (
                <MDBox mt={2}>
                  <MDTypography variant="h6" mb={2}>
                    Daftar File yang Akan Diunggah
                  </MDTypography>
                  <DataTable
                    table={{
                      columns: [
                        { Header: "Nama File", accessor: "name" },
                        {
                          Header: "Ukuran",
                          accessor: "size",
                          Cell: ({ value }) =>
                            `${(value / 1024 / 1024).toFixed(2)} MB`,
                        },
                        {
                          Header: "Tipe",
                          accessor: "type",
                          Cell: ({ value }) =>
                            value.split("/")[1].toUpperCase(),
                        },
                      ],
                      rows: files.map((file) => ({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                      })),
                    }}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              )}
              <Grid item xs={12}>
                {isEdit && (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt={2}
                  >
                    <MDTypography variant="h6" mb={2}>
                      QR Code Aset
                    </MDTypography>
                    {qrCodeData?.qrcode ? (
                      <Card>
                        <MDBox
                          p={2}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <img
                            src={`${API_ESA_OFFICE_UPLOAD}${qrCodeData.qrcode}`}
                            alt="QR Code"
                            style={{
                              width: 200,
                              height: 200,
                              objectFit: "contain",
                            }}
                          />
                          <MDTypography variant="caption" color="text" mt={1}>
                            Scan QR Code untuk melihat detail aset
                          </MDTypography>
                        </MDBox>
                      </Card>
                    ) : (
                      <MDTypography variant="caption" color="text">
                        QR Code tidak tersedia
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              Simpan
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog untuk preview foto */}
        <Dialog
          open={openImageDialog}
          onClose={() => setOpenImageDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Preview Foto</MDTypography>
              <MDButton
                color="secondary"
                onClick={() => setOpenImageDialog(false)}
                size="small"
              >
                Tutup
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={2}>
                {selectedImages.map((foto, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <MDBox p={2}>
                        <img
                          src={`${API_ESA_OFFICE_UPLOAD}${foto}`}
                          alt={`Foto ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                        <MDBox mt={1}>
                          <MDTypography variant="caption" color="text">
                            Foto {index + 1}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </DialogContent>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AsetList;
